// Pure Square order-body builder for quote payment links (franklycatering #826).
// Moved out of square-payments/index.ts so it can be unit-tested by vitest (see
// apps/app/src/test/unit/square-order-body.test.ts). It is also covered by
// tsc and lint as well as deno check, so it carries full types and no `any`.

interface OrderLineItemSource {
  name: string;
  quantity: number | string;
  unit_price: number | string;
  description?: string | null;
  sort_order?: number | null;
}

export interface OrderQuoteSource {
  event_date?: string | null;
  guest_count?: number | null;
  delivery_amount?: number | string | null;
  gratuity_percent?: number | string | null;
  gratuity_amount?: number | string | null;
  line_items?: OrderLineItemSource[] | null;
}

// An untaxed, fixed-amount Square service charge. Square prints each one as its
// own row on the checkout page and the receipt, and `taxable: false` keeps any
// order-scope tax off it.
interface SquareServiceCharge {
  uid: string;
  name: string;
  amount_money: { amount: number; currency: string };
  calculation_phase: "SUBTOTAL_PHASE";
  taxable: false;
}

export const GRATUITY_UID = "gratuity";
export const CARD_SURCHARGE_UID = "card-processing-fee";
const CARD_SURCHARGE_NAME = "Credit Card Processing Fee";

function serviceCharge(uid: string, name: string, amountCents: number, currency: string): SquareServiceCharge {
  return { uid, name, amount_money: { amount: amountCents, currency }, calculation_phase: "SUBTOTAL_PHASE", taxable: false };
}

// 3 -> " (3%)", 2.5 -> " (2.5%)"; empty when there's no usable percentage.
function percentLabel(percent: number | string | null | undefined): string {
  const n = Number(percent);
  return Number.isFinite(n) && n > 0 ? ` (${n}%)` : "";
}

// Build a rich event description line from quote data
function buildEventNote(quote: OrderQuoteSource, eventTypeName?: string): string {
  const parts: string[] = [];
  if (eventTypeName) parts.push(eventTypeName);
  if (quote.event_date) {
    const d = new Date(quote.event_date + "T00:00:00");
    parts.push(d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }));
  }
  if (quote.guest_count) parts.push(`${quote.guest_count} guests`);
  return parts.join(" · ");
}

// Build Square order body — itemized for full payments (plus a "Delivery" line
// when the quote has delivery, and a gratuity service charge when it has one,
// #982), single descriptive line for partial. The partial line is priced off
// quote.total, which already includes the gratuity.
//
// The itemized order carries no tax or discount, so Square would charge less
// than the quote total on any taxed or discounted quote (#799). A card fee
// priced off the total (#982) must sit on top of exactly that total, so a full
// payment is itemized only when the lines add up to it exactly, and is
// otherwise sent as a single line for the rest of the total. Either way a full
// payment keeps its gratuity as its own service charge.
export function buildOrderBody(
  locationId: string,
  quote: OrderQuoteSource,
  amountCents: number,
  totalCents: number,
  currency: string,
  label: string,
  eventTypeName?: string,
) {
  const isFullPayment = amountCents === totalCents;
  // The line_items embed comes back in no guaranteed order. Sort by sort_order
  // (#844) so Square lists the items the way /quote/:id does, which orders by
  // sort_order too. Array.prototype.sort is stable, so ties keep the order
  // they arrived in. A missing sort_order counts as 0, the column's default.
  const lineItems = [...(quote.line_items ?? [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );
  const gratuityCents = isFullPayment ? Math.round(Number(quote.gratuity_amount ?? 0) * 100) : 0;
  const gratuity = gratuityCents > 0 && gratuityCents < amountCents
    ? { service_charges: [serviceCharge(GRATUITY_UID, `Gratuity${percentLabel(quote.gratuity_percent)}`, gratuityCents, currency)] }
    : null;
  const gratuityChargedCents = gratuity ? gratuityCents : 0;

  if (isFullPayment && lineItems.length > 0) {
    const deliveryCents = Math.round(Number(quote.delivery_amount ?? 0) * 100);
    const itemized = {
      location_id: locationId,
      line_items: [
        ...lineItems.map((li) => ({
          name: li.name,
          quantity: String(li.quantity),
          base_price_money: {
            amount: Math.round(Number(li.unit_price) * 100),
            currency,
          },
          ...(li.description ? { note: li.description } : {}),
        })),
        ...(deliveryCents > 0
          ? [{ name: "Delivery", quantity: "1", base_price_money: { amount: deliveryCents, currency } }]
          : []),
      ],
      ...gratuity,
    };
    // What Square will charge for this order. A malformed price makes it NaN,
    // which fails the check and falls back to the single line.
    const itemizedCents =
      itemized.line_items.reduce((sum, li) => sum + li.base_price_money.amount * Number(li.quantity), 0) +
      gratuityChargedCents;
    if (itemizedCents === totalCents) return itemized;
  }

  const eventNote = buildEventNote(quote, eventTypeName);
  return {
    location_id: locationId,
    line_items: [
      {
        name: label,
        quantity: "1",
        base_price_money: { amount: amountCents - gratuityChargedCents, currency },
        ...(eventNote ? { note: eventNote } : {}),
      },
    ],
    ...gratuity,
  };
}

// The card processing fee on a charge of `amountCents` (#982).
export function cardSurchargeCents(amountCents: number, percent: number): number {
  if (!Number.isFinite(amountCents) || amountCents <= 0) return 0;
  if (!Number.isFinite(percent) || percent <= 0) return 0;
  return Math.round((amountCents * percent) / 100);
}

// Splits a card charge that already includes the card fee back into what
// counts toward the quote and the fee (#1138). The inverse of
// withCardSurcharge: net + cardSurchargeCents(net) ≈ gross, and net + fee is
// always exactly gross.
export function splitCardChargeCents(grossCents: number, percent: number): { netCents: number; feeCents: number } {
  if (!Number.isFinite(grossCents) || grossCents <= 0) return { netCents: 0, feeCents: 0 };
  if (!Number.isFinite(percent) || percent <= 0) return { netCents: grossCents, feeCents: 0 };
  const netCents = Math.round((grossCents * 100) / (100 + percent));
  return { netCents, feeCents: grossCents - netCents };
}

// The card fee percent from the cc_surcharge_enabled / cc_surcharge_percent
// app_settings values. Mirrors SQL card_surcharge_percent() in
// 20260930033452_add_quote_gratuity_and_card_surcharge.sql; keep the two in
// step. Anything off, malformed or over 10 reads as 0. Postgres trim() strips
// spaces only, not tabs or newlines, so neither does this.
export function cardSurchargePercentFromSettings(
  enabled: string | null | undefined,
  percent: string | null | undefined,
): number {
  const trimSpaces = (s: string) => s.replace(/^ +| +$/g, "");
  if (trimSpaces(enabled ?? "false").toLowerCase() !== "true") return 0;
  const raw = trimSpaces(percent ?? "");
  if (!/^[0-9]+(\.[0-9]+)?$/.test(raw)) return 0;
  const n = Number(raw);
  return n <= 10 ? n : 0;
}

// Adds the card processing fee as its own service charge, on top of whatever
// the order already charges. `amountCents` is what the link is meant to collect
// for the quote (the deposit, the balance or the full total), so the customer
// pays amountCents plus the fee.
export function withCardSurcharge<T extends { service_charges?: SquareServiceCharge[] }>(
  order: T,
  amountCents: number,
  percent: number,
  currency: string,
): T {
  const feeCents = cardSurchargeCents(amountCents, percent);
  if (feeCents <= 0) return order;
  return {
    ...order,
    service_charges: [
      ...(order.service_charges ?? []),
      serviceCharge(CARD_SURCHARGE_UID, `${CARD_SURCHARGE_NAME}${percentLabel(percent)}`, feeCents, currency),
    ],
  };
}

interface SquareMoneyLike {
  amount?: number | null;
}

// The slice of a Square order (GET /v2/orders/{id}) that quoteCreditCents reads.
export interface SquareOrderLike {
  total_money?: SquareMoneyLike | null;
  service_charges?: Array<{
    uid?: string | null;
    name?: string | null;
    amount_money?: SquareMoneyLike | null;
    applied_money?: SquareMoneyLike | null;
    total_money?: SquareMoneyLike | null;
  }> | null;
}

// How much of a Square payment counts toward the quote: the payment less the
// card fee on its order. The fee is the customer's cost of paying by card, not
// part of quote.total, so crediting it would make amount_paid overshoot and the
// balance come up short. A payment that covers only part of the order carries
// the same share of the fee.
//
// The fee is found by the uid withCardSurcharge gives it, or by its name as a
// fallback. A Square-sourced quote (squareSourced) can also be paid through one
// of our links, but its invoice may carry charges of its own that are part of
// quote.total, so for those only the uid counts.
//
// Square can also add a card fee of its own to a Square-sourced invoice
// (#1121). That fee isn't on the order, so the payment comes in above the
// order's total_money, which is what the invoice sync copies into quote.total.
// Everything the quote is owed is on the order, so a Square-sourced payment
// is credited no more than the order total.
export function quoteCreditCents(
  paymentCents: number,
  order: SquareOrderLike | null | undefined,
  { squareSourced = false }: { squareSourced?: boolean } = {},
): number {
  const feeCents = (order?.service_charges ?? [])
    .filter((sc) => sc.uid === CARD_SURCHARGE_UID || (!squareSourced && (sc.name ?? "").startsWith(CARD_SURCHARGE_NAME)))
    .reduce((sum, sc) => sum + Number((sc.total_money ?? sc.applied_money ?? sc.amount_money)?.amount ?? 0), 0);
  const orderCents = Number(order?.total_money?.amount ?? 0);
  if (!(feeCents > 0)) {
    return squareSourced && orderCents > 0 ? Math.min(paymentCents, orderCents) : paymentCents;
  }
  const share = orderCents > 0 ? Math.round((feeCents * Math.min(paymentCents, orderCents)) / orderCents) : feeCents;
  return Math.max(0, paymentCents - share);
}
